import { VictronImage } from '@components/display/images';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    alpha,
    Button,
    ButtonBase,
    ButtonBaseProps,
    ButtonProps,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import clsx from 'clsx';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

type ProductCardProps = ButtonBaseProps & {
    loading?: boolean;
    baseUrl?: string;
    slug?: string;
    prefetchFn?: (slug: string) => void;
    image: {
        url: string;
        alt_text: string;
    };
    date?:
        | string
        | {
              start: string;
              end: string;
          };
    title: string;
    link?: {
        text?: React.ReactElement;
        /**
         * In case it's to redirect to a URL
         */
        url?: string;
        /**
         * In case it's to do something other than just redirecting, i.e. opening a modal
         */
        fn?: (slug: string) => void;
        icon?: ButtonProps['endIcon'];
        loading?: boolean;
    };
    enlarge?: boolean;
};

export const ProductCard = ({
    loading,
    baseUrl,
    slug,
    prefetchFn,
    image,
    date,
    title,
    link,
    enlarge = false,
    ...buttonBaseProps
}: ProductCardProps) => {
    const isReactLink = baseUrl?.startsWith('/app');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const Image = (
        <div className="relative w-full aspect-[calc(16/9)]">
            <VictronImage
                src={image.url}
                alt={image.alt_text}
                className={clsx(
                    'h-full w-full rounded-md object-cover transition-transform duration-300 ease-out-quad',
                    {
                        'group-hover:scale-105': enlarge,
                    }
                )}
            />
        </div>
    );

    const isLink = !!link && !link.fn;

    const componentProps =
        isMobile && link?.url
            ? {
                  component: 'a',
                  href: link.url,
                  target: '_blank',
              }
            : !isLink && slug
            ? {
                  component: 'button',
                  onClick: () => link?.fn?.(slug),
              }
            : isReactLink && slug
            ? {
                  component: Link,
                  to: `${baseUrl}${slug}`,
                  onMouseEnter: () => prefetchFn?.(slug),
                  target: '_blank',
              }
            : slug
            ? {
                  component: 'a',
                  href: `${baseUrl}${slug}`,
                  target: '_blank',
              }
            : {};

    const CardContent = (
        <Stack direction="column" height="100%" width="100%" textAlign="left" className="group">
            {loading ? (
                <Skeleton variant="rectangular" width="100%" className="rounded-md">
                    {Image}
                </Skeleton>
            ) : baseUrl ? (
                isReactLink && slug ? (
                    <Link to={`${baseUrl}${slug}`} onMouseEnter={() => prefetchFn?.(slug)} target="_blank">
                        {Image}
                    </Link>
                ) : slug ? (
                    <a href={`${baseUrl}${slug}`} target="_blank">
                        {Image}
                    </a>
                ) : (
                    Image
                )
            ) : (
                <div className="block w-full aspect-[calc(16/9)] p-0 bg-transparent">{Image}</div>
            )}
            {loading ? (
                <Skeleton className="mt-[16px]">
                    <Typography>dd MMM, yyyy</Typography> {/* Placeholder for date text, no need for i18n */}
                </Skeleton>
            ) : (
                date && (
                    <Typography mt="16px" fontSize={14} fontWeight={500} lineHeight="19.2px" color={alpha('#000', 0.4)}>
                        {(() => {
                            if (typeof date === 'string') {
                                return format(date, 'dd MMM yyyy');
                            }
                            const startDateLabel = format(date.start, 'dd MMM yyyy');
                            const endDateLabel = format(date.end, 'dd MMM yyyy');

                            return startDateLabel === endDateLabel
                                ? startDateLabel
                                : `${startDateLabel} - ${endDateLabel}`;
                        })()}
                    </Typography>
                )
            )}
            {loading ? (
                <Skeleton variant="text" className="my-[12px]">
                    <Typography lineHeight="19.2px" className="line-clamp-1">
                        Loading title, which is very big... {/* Placeholder for title text, no need for i18n */}
                    </Typography>
                </Skeleton>
            ) : (
                <Typography my="12px" fontSize={16} fontWeight={500} lineHeight="19.2px" className="line-clamp-1">
                    {loading ? <Skeleton /> : title}
                </Typography>
            )}
            {loading ? (
                <Skeleton>
                    <Typography>Read More</Typography> {/* Placeholder for link text, no need for i18n */}
                </Skeleton>
            ) : (
                link && (
                    <div className="mt-auto">
                        <Button
                            className="p-0 font-medium"
                            variant="text"
                            endIcon={link.icon ?? <ArrowForwardIcon />}
                            classes={{
                                endIcon: 'transition-transform duration-300 ease-out-quad group-hover:translate-x-1',
                                text: 'text-left',
                            }}
                            loading={link.loading}
                        >
                            {link?.text ?? <FormattedMessage id="common.actions.readMore" defaultMessage="Read more" />}
                        </Button>
                    </div>
                )
            )}
        </Stack>
    );

    if (loading || !link) {
        return CardContent;
    }

    return (
        <ButtonBase
            {...componentProps}
            focusRipple
            {...buttonBaseProps}
            className={clsx('w-full h-full rounded-lg', buttonBaseProps.className)}
        >
            {CardContent}
        </ButtonBase>
    );
};
